import { z } from "zod";

export const DebugFilter = z.object({
  projectIds: z.array(z.string()),
  connectionIds: z.array(z.string()),
  roles: z.array(z.string()),
  eventTypes: z.array(z.string()),
  userIds: z.array(z.string()),
});

export type DebugFilter = z.infer<typeof DebugFilter>;

export const DEFAULT_FILTER: DebugFilter = {
  projectIds: [],
  connectionIds: [],
  roles: [],
  eventTypes: [],
  userIds: [],
};

export interface DebugMessage {
  type: string;
  timestamp: number;
  sender: {
    connectionId: string;
    userId: string;
    role: string;
  };
  original: {
    type: string;
    [key: string]: unknown;
  };
  channels?: string[];
  projectId?: string;
}

export function matchesDebugFilter(msg: DebugMessage, filter: DebugFilter): boolean {
  if (filter.projectIds.length > 0 && (!msg.projectId || !filter.projectIds.includes(msg.projectId))) {
    return false;
  }
  if (filter.connectionIds.length > 0 && !filter.connectionIds.includes(msg.sender.connectionId)) {
    return false;
  }
  if (filter.roles.length > 0 && !filter.roles.includes(msg.sender.role)) {
    return false;
  }
  if (filter.eventTypes.length > 0 && !filter.eventTypes.includes(msg.original.type)) {
    return false;
  }
  if (filter.userIds.length > 0 && !filter.userIds.includes(msg.sender.userId)) {
    return false;
  }
  return true;
}

export const KNOWN_EVENT_TYPES = [
  "session-location",
  "session-location-ack",
  "session-location-error",
  "position-update",
  "session-started",
  "session-ended",
  "heartbeat",
  "block-declared",
  "block-released",
  "note-added",
  "photo-added",
  "identity-updated",
  "sos-ping",
  "sos-started",
  "sos-cancelled",
  "sos-reopened",
  "sos-resolved",
  "sos-message",
  "sos-photo",
  "sos-ack",
  "quick-message",
  "sos-dispatch-created",
  "sos-dispatch-invited",
  "sos-dispatch-accepted",
  "sos-dispatch-declined",
  "sos-dispatch-completed",
  "sos-message-read",
  "rendezvous-request",
  "rendezvous-accepted",
  "rendezvous-declined",
  "rendezvous-update",
  "message-sent",
] as const;

export const EVENT_CATEGORIES: Record<string, readonly string[]> = {
  "SOS": [
    "sos-started", "sos-cancelled", "sos-reopened", "sos-resolved",
    "sos-message", "sos-photo", "sos-ack", "sos-ping",
    "sos-dispatch-created", "sos-dispatch-invited", "sos-dispatch-accepted",
    "sos-dispatch-declined", "sos-dispatch-completed", "sos-message-read",
  ],
  "Operational": [
    "session-location", "session-location-ack", "session-location-error",
    "position-update", "session-started", "session-ended", "heartbeat",
  ],
  "Commands": [
    "block-declared", "block-released", "note-added", "photo-added", "identity-updated",
  ],
  "Messaging": [
    "quick-message", "message-sent",
  ],
  "Rendezvous": [
    "rendezvous-request", "rendezvous-accepted", "rendezvous-declined", "rendezvous-update",
  ],
} as const;
